import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import moment from "moment";
import styles from "./List.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { fetchStaffClassAndSubjects } from "../../../redux/Masterdata";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomTextInput,
} from "../../../components";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";

const LeaveList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const staff_id = clientData?.master_id || "";
  const school_id = clientData?.school_id || "";

  const { translations } = useSelector((state) => state.language);

  // Redux masterData for class dropdown
  const {
    staffClassAndSubjectOptions: reduxClassOptions,
    staffClassAndSubjectLoading: classLoading,
  } = useSelector((state) => state.masterData);

  // Build class dropdown: "Class - Section" combined (deduplicated)
  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        const key = `${item.class_id}-${item.section_id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: `${item.class_id}-${item.section_id}`,
        label: `${item.class_name} - ${item.section_id}`,
        class_id: item.class_id,
        section_id: item.section_id,
      }));
  }, [reduxClassOptions]);

  // Status dropdown options — values as numbers: 0=Submitted, 1=Accepted, 2=Rejected
  const statusOptions = useMemo(
    () => [
      {
        value: "0",
        label: translations.labels?.submitted || "Submitted",
      },
      {
        value: "1",
        label: translations.labels?.accepted || "Accepted",
      },
      {
        value: "2",
        label: translations.labels?.rejected || "Rejected",
      },
    ],
    [translations],
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Filter state
  const [filters, setFilters] = useState({
    studentId: "",
    classSection: "",
    fromDate: null,
    toDate: null,
    status: "",
  });

  // Fetch staff class list on mount
  useEffect(() => {
    if (staff_id) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(staff_id),
          class_id: "",
          section_id: "",
          subject_id: "",
          class_staff: 1,
        }),
      );
    }
  }, [dispatch, staff_id]);

  // Fetch leave list on mount
  useEffect(() => {
    if (staff_id) {
      fetchLeaveList();
    }
  }, [staff_id]);

  const fetchLeaveList = async (filterParams = {}) => {
    setLoading(true);
    try {
      // Parse class-section from combined value
      let classId = "";
      let sectionId = "";
      if (filterParams.classSection) {
        const selected = classOptions.find(
          (c) => c.value === filterParams.classSection,
        );
        if (selected) {
          classId = selected.class_id;
          sectionId = selected.section_id;
        }
      }

      const payload = {
        staff_id: parseInt(staff_id),
        student_id: filterParams.studentId || "",
        class_id: classId ? parseInt(classId) : "",
        section_id: sectionId || "",
        from_date: filterParams.fromDate || "",
        to_date: filterParams.toDate || "",
        status:
          filterParams.status !== undefined && filterParams.status !== ""
            ? parseInt(filterParams.status)
            : "",
      };

      const response = await AxiosInstance.post(
        `/attendance/staff/leaves`,
        payload,
      );

      if (response?.data?.status) {
        const leaveData = response.data.data || [];
        const transformedData = leaveData.map((item, index) => ({
          id: item.leave_id,
          sno: index + 1,
          student_id: item.student_id,
          stud_id: item.stud_id || "",
          student_name: item.student_name || "",
          class_id: item.class_id,
          section_id: item.section_id,
          from_date: item.from_date
            ? moment(item.from_date).format("DD/MM/YYYY")
            : "",
          to_date: item.to_date
            ? moment(item.to_date).format("DD/MM/YYYY")
            : "",
          no_days: item.no_days || 0,
          reason: item.reason || "",
          status: item.status,
          rattch_status: item.rattch_status || 0,
          isFlag: item.isFlag || 0,
        }));
        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching leave list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return {
          label: translations.labels?.accepted || "ACCEPTED",
          className: styles.statusAccepted,
        };
      case 2:
        return {
          label: translations.labels?.rejected || "REJECTED",
          className: styles.statusRejected,
        };
      case 4:
        return {
          label: translations.labels?.withdrawn || "WITHDRAWN",
          className: styles.statusWithdrawn,
        };
      default:
        return {
          label: translations.labels?.submitted || "SUBMITTED",
          className: styles.statusSubmitted,
        };
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.student_name?.toLowerCase().includes(searchLower) ||
        String(item.stud_id)?.toLowerCase().includes(searchLower) ||
        item.from_date?.toLowerCase().includes(searchLower) ||
        item.to_date?.toLowerCase().includes(searchLower) ||
        item.reason?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  // Filter handlers
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterDateChange = (name, date) => {
    setFilters((prev) => ({
      ...prev,
      [name]: date ? dayjs(date).format("YYYY-MM-DD") : null,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    fetchLeaveList(filters);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleReset = () => {
    setFilters({
      studentId: "",
      classSection: "",
      fromDate: null,
      toDate: null,
      status: "",
    });
    fetchLeaveList({});
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleSelect = (row) => {
    navigate("/user/leaveApproval", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "stud_id",
        header: translations.labels?.studentId || "Student id",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "student_name",
        header: translations.labels?.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row.original.isFlag === 1 && (
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "red",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            )}
            <span>{row.original.student_name}</span>
          </div>
        ),
      },
      {
        accessorKey: "from_date",
        header: translations.labels?.fromDate || "From Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "to_date",
        header: translations.labels?.toDate || "To Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "no_days",
        header: translations.labels?.noOfDaysLeave || "No. of Days",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "reason",
        header: translations.labels?.reason || "Reason",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: translations.labels?.status || "Status",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const badge = getStatusBadge(row.original.status);
          return <span className={badge.className}>{badge.label}</span>;
        },
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleSelect(row.original)}
            title={translations.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.leaveList || "Leave List"}
        </h1>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          {/* Student ID */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.studentId || "Student id"}
              name="studentId"
              type="text"
              value={filters.studentId}
              onChange={handleFilterInputChange}
              placeholder={
                translations.labels?.searchStudentId || "Search student id..."
              }
            />
          </div>

          {/* Class Dropdown */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="classSection"
              value={filters.classSection}
              onChange={(value) => handleFilterChange("classSection", value)}
              options={classOptions}
              placeholder={
                translations.labels?.classPlaceholder || "Select Class"
              }
              isSearchable={false}
              loading={classLoading}
            />
          </div>

          {/* From Date */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDatePicker
              label={translations.labels?.fromDate || "From Date"}
              name="fromDate"
              value={filters.fromDate ? dayjs(filters.fromDate) : null}
              onChange={(date) => handleFilterDateChange("fromDate", date)}
              placeholder="Ex: 30/07/2016"
            />
          </div>

          {/* To Date */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDatePicker
              label={translations.labels?.toDate || "To Date"}
              name="toDate"
              value={filters.toDate ? dayjs(filters.toDate) : null}
              onChange={(date) => handleFilterDateChange("toDate", date)}
              placeholder="Ex: 30/07/2016"
            />
          </div>

          {/* Status Dropdown */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.status || "Status"}
              name="status"
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              options={statusOptions}
              placeholder={translations.labels?.selectStatus || "Select Status"}
              isSearchable={false}
            />
          </div>

          {/* Search & Reset Buttons */}
          <div className="col-lg-2 px-2 col-md-6 col-sm-12 ">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleSearch}
              >
                {translations.labels?.searchButton || "SEARCH"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                onClick={handleReset}
              >
                {translations.labels?.resetButton || "RESET"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        placeholder={translations.labels.searchButton}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={false}
        enableFiltering={false}
      />
    </div>
  );
};

export default LeaveList;
